import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import api from "./util";

const AuthContext = createContext();

const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [authTokens, setAuthTokens] = useState(() =>
    localStorage.getItem("tokens")
      ? JSON.parse(localStorage.getItem("tokens"))
      : null
  );
  const [refreshToken, setRefreshToken] = useState(() =>
    localStorage.getItem("refreshToken")
      ? JSON.parse(localStorage.getItem("refreshToken"))
      : null
  );
  const [user, setUser] = useState(() =>
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null
  );

  // useRef keeps latest values accessible inside the interval
  // without adding them as dependencies (which would restart the interval)
  const refreshTokenRef = useRef(refreshToken);
  const userRef = useRef(user);
  useEffect(() => { refreshTokenRef.current = refreshToken; }, [refreshToken]);
  useEffect(() => { userRef.current = user; }, [user]);

  const logoutUser = useCallback(() => {
    setAuthTokens(null);
    setRefreshToken(null);
    setUser(null);
    localStorage.removeItem("tokens");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("selectedCompany");
    sessionStorage.clear();
  }, []);

  // useCallback ensures this function reference is stable
  const refreshTokenFunction = useCallback(async () => {
    const currentRefreshToken = refreshTokenRef.current;
    const currentUser = userRef.current;

    if (!currentRefreshToken) {
      logoutUser();
      return;
    }

    try {
      const response = await api.post("/login/refresh-token", {
        refreshToken: currentRefreshToken,
      });

      if (response.status === 201) {
        const { data } = response;
        const decodedToken = decodeToken(data.token);
        const userData = {
          email: currentUser?.email || decodedToken.user_id,
          user_id: decodedToken.user_id,
          company_id: decodedToken.company_id,
          role_id: decodedToken.role_id,
        };

        setAuthTokens(data.token);
        setUser(userData);
        localStorage.setItem("tokens", JSON.stringify(data.token));
        localStorage.setItem("user", JSON.stringify(userData));

        if (decodedToken.role_id === 1 && !localStorage.getItem("selectedCompany")) {
          localStorage.setItem("selectedCompany", "1");
        }
      } else {
        logoutUser();
      }
    } catch (err) {
      console.log("refresh token error", err);
      logoutUser();
    }
  }, [logoutUser]); // stable — logoutUser is also useCallback

  useEffect(() => {
    // Only start the interval if there's a refresh token
    if (!refreshToken) return;

    const interval = setInterval(() => {
      refreshTokenFunction();
    }, 1000 * 60); // every 1 minute

    return () => clearInterval(interval);
  }, []); // ← empty deps: interval is set once on mount, uses refs internally

  const loginUser = async (email, password) => {
    try {
      const response = await api.post("/login", { email, password });

      if (response.data && response.data.token) {
        const { data } = response;
        const decodedToken = decodeToken(data.token);
        const userData = {
          email,
          user_id: decodedToken.user_id,
          company_id: decodedToken.company_id,
          role_id: decodedToken.role_id,
        };

        setAuthTokens(data.token);
        setRefreshToken(data.refreshToken);
        setUser(userData);

        localStorage.setItem("tokens", JSON.stringify(data.token));
        localStorage.setItem("refreshToken", JSON.stringify(data.refreshToken));
        localStorage.setItem("user", JSON.stringify(userData));

        if (decodedToken.role_id === 1) {
          localStorage.setItem("selectedCompany", "1");
        } else {
          localStorage.setItem("selectedCompany", String(decodedToken.company_id));
        }

        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.log("login error", err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{ authTokens, user, loginUser, logoutUser, refreshTokenFunction }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;